import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type BillingMode = 'stripe' | 'free' | 'comped' | 'lr_direct' | 'lr_direct_private' | 'board_only';

/**
 * Returns the billing_mode of the current user's primary company subscription.
 * Drives ad slots, upgrade CTAs, and any other paid-vs-free feature gating.
 */
export function useBillingMode() {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const query = useQuery({
    queryKey: ['billing-mode', userId],
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<BillingMode | null> => {
      if (!userId) return null;
      // Never use .maybeSingle() here: a duplicated membership row used to make
      // this return null, which silently downgraded a PAYING user to free.
      const { data: memberships } = await supabase
        .from('company_users')
        .select('company_id, is_primary, joined_at')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('is_primary', { ascending: false })
        .order('joined_at', { ascending: false });

      const companyIds = Array.from(new Set((memberships ?? []).map((m) => m.company_id)));
      if (companyIds.length === 0) return null;
      if (companyIds.length > 1) {
        console.warn('[useBillingMode] user has multiple active companies', companyIds);
      }

      const { data: subs } = await supabase
        .from('subscriptions')
        .select('billing_mode, status, created_at, company_id')
        .in('company_id', companyIds);

      const rows = (subs ?? []) as Array<{ billing_mode: string | null; status: string | null; created_at: string | null }>;
      if (rows.length === 0) return null;
      if (rows.length > 1) {
        console.warn('[useBillingMode] multiple subscription rows found — picking the most permissive active one', rows.length);
      }

      // Prefer live subscriptions, then the most permissive mode, then newest.
      const PERMISSIVENESS: Record<string, number> = {
        lr_direct: 5, comped: 4, stripe: 3, lr_direct_private: 2, board_only: 1, free: 0,
      };
      const rank = (r: typeof rows[number]) => [
        r.status === 'active' || r.status === 'trialing' ? 1 : 0,
        PERMISSIVENESS[r.billing_mode ?? 'free'] ?? 0,
        r.created_at ? Date.parse(r.created_at) : 0,
      ];
      const best = rows.slice().sort((a, b) => {
        const ra = rank(a), rb = rank(b);
        for (let i = 0; i < ra.length; i++) if (ra[i] !== rb[i]) return rb[i] - ra[i];
        return 0;
      })[0];

      return (best?.billing_mode as BillingMode) ?? null;
    },

  });

  const billingMode = query.data ?? null;
  const isFree = billingMode === 'free';
  // Broker tier ladder (v5):
  //   board_only ($59.99)  → LaneRunner load board only, no LR Direct bidding
  //   stripe     ($99.99)  → Full Access: private-network + open-market LR Direct
  //   lr_direct  ($249.99) → RETIRED tier, legacy subscribers keep full access
  //   lr_direct_private    → RETIRED (no new signups; treated as no LR Direct access)
  const isBoardOnly = billingMode === 'board_only';
  const isLRDirectPrivate = billingMode === 'lr_direct_private'; // retired legacy value
  const isLRDirectFull = billingMode === 'lr_direct';
  // LR Direct UI/route access. board_only is explicitly excluded.
  const hasAnyLRDirectAccess =
    isLRDirectFull ||
    billingMode === 'stripe' ||
    billingMode === 'comped';
  // Premium load-board tier (no ads, AI features). board_only is a paid tier,
  // so it gets the ad-free treatment too.
  const isPaidPremium =
    billingMode === 'stripe' ||
    billingMode === 'comped' ||
    billingMode === 'lr_direct' ||
    billingMode === 'board_only';
  // Free load-board treatment (ads visible). Only true free users now.
  const isFreeBoardTier = isFree;
  // Private-network LR Direct bidding: $99.99 and up.
  const hasPrivateNetworkAccess =
    billingMode === 'stripe' ||
    billingMode === 'comped' ||
    billingMode === 'lr_direct';
  // Open-market LR Direct bidding: Full Access ($99.99 = 'stripe'), comped, and
  // the retired $249.99 'lr_direct' tier. board_only / free stay excluded.
  const hasLRDirectFullAccess =
    billingMode === 'stripe' ||
    billingMode === 'lr_direct' ||
    billingMode === 'comped';


  return {
    billingMode,
    isFree,
    isBoardOnly,
    isLRDirectPrivate,
    isLRDirectFull,
    hasAnyLRDirectAccess,
    hasPrivateNetworkAccess,
    hasLRDirectFullAccess,
    isPaidPremium,
    isFreeBoardTier,
    // Back-compat: any paying subscription
    isPaid: billingMode === 'stripe' || billingMode === 'comped' || billingMode === 'lr_direct' || billingMode === 'board_only',
    isLoading: query.isLoading,
  };
}
